import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as fromClaims from '../actions/claims.actions';
import * as fromInvolvedParties from '../actions/involvedParties.actions';
import { InvolvedPartiesService } from './involvedParties.service';
import { PartyRolesEnum } from '@app/core';

@Injectable()
export class InvolvedPartiesEffects {

    public partyRoles: typeof PartyRolesEnum = PartyRolesEnum;

     load$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(fromInvolvedParties.LOAD),
        switchMap((action: fromInvolvedParties.Load) =>
            this.involvedPartiesService.getInvolvedParties(
                action.payload.relatie_id,
                action.payload.dossier_volgnummer,
                ).pipe(
                map((resultaat) => resultaat.data),
                map((payload) => new fromInvolvedParties.LoadSuccess({
                    dossier_volgnummer: action.payload.dossier_volgnummer,
                    entity: payload,
                })),
                catchError((errors) => of(new fromInvolvedParties.SetErrors({
                    dossier_volgnummer: action.payload.dossier_volgnummer,
                    errors: errors,
                }))),
            ),
        ),
    ));

     setDriver$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(fromInvolvedParties.SET_DRIVER),
        switchMap((action: fromInvolvedParties.SetDriver) =>
            this.involvedPartiesService.setDriver(
                action.payload.dossier_volgnummer,
                action.payload.persoon,
                ).pipe( switchMap(() => {
                    return [
                        new fromInvolvedParties.UpdateNaturalPerson({
                            dossier_volgnummer: action.payload.dossier_volgnummer,
                            persoon: action.payload.persoon,
                            rol: {
                                rol: this.partyRoles.Driver,
                                rol_omschrijving: 'Bestuurder',
                            },
                        }),
                        new fromClaims.UpdateInvolvedPartiesActions({
                            dossier_volgnummer: action.payload.dossier_volgnummer,
                            type: 'bestuurder',
                        }),
                        new fromInvolvedParties.UpdateBetrokkene({
                            dossier_volgnummer: action.payload.dossier_volgnummer,
                            type: 'bestuurder',
                            betrokkene: {
                                meerdere_toegestaan_indicatie: 'N',
                                toevoegen_toegestaan_indicatie: 'N',
                                meerdere_toegestaan_indicatie_omschrijving: 'Nee',
                            },
                        }),
                    ];
                }),
                catchError((errors) => of(new fromInvolvedParties.SetErrors({
                    dossier_volgnummer: action.payload.dossier_volgnummer,
                    errors: errors,
                }))),
            ),
        ),
    ));

     setWitness$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(fromInvolvedParties.SET_WITNESS),
        switchMap((action: fromInvolvedParties.SetWitness) =>
            this.involvedPartiesService.setWitness(
                action.payload.relatie_id,
                action.payload.dossier_volgnummer,
                action.payload.persoon,
                ).pipe( switchMap(() => {
                    return [
                        new fromInvolvedParties.UpdateNaturalPerson({
                            dossier_volgnummer: action.payload.dossier_volgnummer,
                            persoon: action.payload.persoon,
                            rol: {
                                rol: this.partyRoles.Witness,
                                rol_omschrijving: 'Getuige',
                            },
                        }),
                        new fromClaims.UpdateInvolvedPartiesActions({
                            dossier_volgnummer: action.payload.dossier_volgnummer,
                            type: 'getuigen',
                        }),
                    ];
                }),
                catchError((errors) => of(new fromInvolvedParties.SetErrors({
                    dossier_volgnummer: action.payload.dossier_volgnummer,
                    errors: errors,
                }))),
            ),
        ),
    ));

     setContact$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(fromInvolvedParties.SET_CONTACT),
        switchMap((action: fromInvolvedParties.SetContact) =>
            this.involvedPartiesService.setContact(
                action.payload.relatie_id,
                action.payload.dossier_volgnummer,
                action.payload.persoon,
                ).pipe( switchMap(() => {
                    return [
                        new fromInvolvedParties.UpdateNaturalPerson({
                            dossier_volgnummer: action.payload.dossier_volgnummer,
                            persoon: action.payload.persoon,
                            rol: {
                                rol: this.partyRoles.Contact,
                                rol_omschrijving: 'Contactpersoon',
                            },
                        }),
                        new fromClaims.UpdateInvolvedPartiesActions({
                            dossier_volgnummer: action.payload.dossier_volgnummer,
                            type: 'contactpersoon',
                        }),
                        new fromInvolvedParties.UpdateBetrokkene({
                            dossier_volgnummer: action.payload.dossier_volgnummer,
                            type: 'contactpersoon',
                            betrokkene: {
                                meerdere_toegestaan_indicatie: 'N',
                                toevoegen_toegestaan_indicatie: 'N',
                                meerdere_toegestaan_indicatie_omschrijving: 'Nee',
                            },
                        }),
                    ];
                }),
                catchError((errors) => of(new fromInvolvedParties.SetErrors({
                    dossier_volgnummer: action.payload.dossier_volgnummer,
                    errors: errors,
                }))),
            ),
        ),
    ));

    // CoInshured
     setCoInshured$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(fromInvolvedParties.SET_CO_INSURED),
        switchMap((action: fromInvolvedParties.SetCoInsured) =>
            this.involvedPartiesService.setCoInsured(
                action.payload.relatie_id,
                action.payload.dossier_volgnummer,
                action.payload.persoon,
                ).pipe( switchMap(() => {
                    return [
                        new fromInvolvedParties.UpdateNaturalPerson({
                            dossier_volgnummer: action.payload.dossier_volgnummer,
                            persoon: action.payload.persoon,
                            rol: {
                                rol: this.partyRoles.CoInsured,
                                rol_omschrijving: 'Medeverzekerde',
                            },
                        }),
                        new fromClaims.UpdateInvolvedPartiesActions({
                            dossier_volgnummer: action.payload.dossier_volgnummer,
                            type: 'medeverzekerde',
                        }),
                    ];
                }),
                catchError((errors) => of(new fromInvolvedParties.SetErrors({
                    dossier_volgnummer: action.payload.dossier_volgnummer,
                    errors: errors,
                }))),
            ),
        ),
    ));

     setCounterParty$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(fromInvolvedParties.SET_COUNTER_PARTY),
        switchMap((action: fromInvolvedParties.SetCounterParty) =>
            this.involvedPartiesService.setCounterParty(
                action.payload.dossier_volgnummer,
                action.payload.persoon,
                action.payload.np_of_nnp_code,
                ).pipe( switchMap(() => {
                    return [
                        new fromInvolvedParties.UpdateNaturalPerson({
                            dossier_volgnummer: action.payload.dossier_volgnummer,
                            persoon: action.payload.persoon,
                            rol: {
                                rol: this.partyRoles.CounterParty,
                                rol_omschrijving: 'Tegenpartij',
                            },
                        }),
                        new fromClaims.UpdateInvolvedPartiesActions({
                            dossier_volgnummer: action.payload.dossier_volgnummer,
                            type: 'tegenpartij',
                        }),
                    ];
                }),
                catchError((errors) => of(new fromInvolvedParties.SetErrors({
                    dossier_volgnummer: action.payload.dossier_volgnummer,
                    errors: errors,
                }))),
            ),
        ),
    ));

     setDriverCounterParty$: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType(fromInvolvedParties.SET_DRIVER_COUNTER_PARTY),
        switchMap((action: fromInvolvedParties.SetDriverCounterParty) =>
            this.involvedPartiesService.setDriverCounterParty(
                action.payload.dossier_volgnummer,
                action.payload.persoon,
                action.payload.tegenpartij_volgnummer,
                ).pipe( switchMap(() => {
                    return [
                        new fromInvolvedParties.UpdateDriverNotNaturalPerson({
                            dossier_volgnummer: action.payload.dossier_volgnummer,
                            persoon: action.payload.persoon,
                            adres_relatie_id: action.payload.adres_relatie_id,
                        }),
                        new fromClaims.UpdateInvolvedPartiesActions({
                            dossier_volgnummer: action.payload.dossier_volgnummer,
                            type: 'tegenpartij',
                        }),
                    ];
                }),
                catchError((errors) => of(new fromInvolvedParties.SetErrors({
                    dossier_volgnummer: action.payload.dossier_volgnummer,
                    errors: errors,
                }))),
            ),
        ),
    ));

    constructor(
        private actions$: Actions,
        private involvedPartiesService: InvolvedPartiesService,
    ) {}
}
